import { useAuth0 } from "@auth0/auth0-react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { customClient } from "@utils/helpers";
import { useEffect, useMemo } from "react";

import {
  ChatType,
  ChatsFilter,
  GetExpertChatMembersDocument,
  GetExpertChatMembersQuery,
} from "@api/graphql/types-and-hooks";

const PAGE_SIZE = 20;

interface UseExpertChatMembersParams {
  filter: ChatsFilter;
  enabled?: boolean;
  /**
   * Eagerly load every page. Use for the bounded open caseload, where the tabs
   * split the full set client-side (Active vs Waiting) — paginating it would
   * leave a tab empty and unscrollable, so later pages never load. Leave false
   * for the archived list, which can be large and pages in on scroll.
   */
  fetchAllPages?: boolean;
}

/**
 * Cursor-paginated list of the current provider's expert chats via the
 * chatMembers connection (PAR-2194). Filtering (status/type/search) is applied
 * server-side through `filter`; pages are fetched on scroll (`fetchNextPage`),
 * or eagerly when `fetchAllPages` is set.
 */
export const useExpertChatMembers = ({ filter, enabled = true, fetchAllPages = false }: UseExpertChatMembersParams) => {
  const { getAccessTokenSilently } = useAuth0();

  const query = useInfiniteQuery({
    queryKey: ["getExpertChatMembers", filter],
    queryFn: async ({ pageParam }) => {
      const token = await getAccessTokenSilently();
      const response = (await customClient({
        token,
        document: GetExpertChatMembersDocument,
        variables: { filter, first: PAGE_SIZE, after: pageParam.after },
      })) as GetExpertChatMembersQuery;
      return response.chatMembers;
    },
    initialPageParam: { after: null as string | null | undefined },
    getNextPageParam: (lastPage) =>
      lastPage?.pageInfo?.hasNextPage ? { after: lastPage.pageInfo.endCursor } : undefined,
    enabled,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const { hasNextPage, fetchNextPage, isFetchingNextPage, error } = query;
  useEffect(() => {
    if (fetchAllPages && enabled && hasNextPage && !isFetchingNextPage && !error) {
      fetchNextPage();
    }
  }, [fetchAllPages, enabled, hasNextPage, isFetchingNextPage, error, fetchNextPage]);

  const chats: ChatType[] = useMemo(
    () =>
      query.data?.pages.flatMap(
        (page) => page?.edges?.map((edge) => edge?.node?.chat).filter((chat): chat is ChatType => !!chat) || [],
      ) || [],
    [query.data],
  );

  return {
    chats,
    isLoading: query.isLoading,
    error: query.error,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    isFetchingMore: query.isFetchingNextPage,
    refetch: query.refetch,
  };
};
